<template>
	<modal
		:active="active"
		:dismissable="true"
		title="Edit group icon"
        :subtitle="group ? group.reportName : null"
        :loading="loading"
        class="edit-group-icon-modal"
		@dismiss="dismiss"
	>

		<modal-body v-if="ready">

            <div class="row">
                <div class="col col-left">
                    <control-group title="Icon style" orientation="vertical">
                        <radio :inline="true" label="Text" name="avatartype" v-model="avatarType" :value="1" />
                        <radio :inline="true" label="Custom icon" name="avatartype" v-model="avatarType" :value="2" />
                    </control-group>

                    <div v-show="avatarType == 1">
                        <control-group :valid="!$v.iconText.$error" title="Icon text" orientation="vertical">
                            <div class="color-picker-editor">
                                <dropdown :dismissable="false" align="end">
                                    <button slot="toggle" type="button" class="btn-color-picker-toggle">
					                    <div class="color-picker-preview" :style="{ 'background-color': iconTextColorCssValue }" />
                                        <div class="color-picker-preview-transparency" />
                                        <tooltip title="Text color" />
				                    </button>

                                    <sketch-picker v-model="iconTextColor" />
                                </dropdown>

                                <input
                                    v-control.block
                                    v-model="iconText"
                                    @input="$v.iconText.$touch()"
                                />
                            </div>

                            <control-info v-if="!$v.iconText.$error">Up to three characters</control-info>
                            <template v-else>
                                <control-error v-if="!$v.iconText.maxLength">Choose up to three characters</control-error>
                            </template>
                        </control-group>

                        <control-group title="Background color" orientation="vertical">
                            <div class="color-picker-editor">
                                <dropdown :dismissable="false" align="end">
                                    <button slot="toggle" type="button" class="btn-color-picker-toggle">
					                    <div class="color-picker-preview" :style="{ 'background-color': iconBackgroundColorCssValue }" />
                                        <div class="color-picker-preview-transparency" />
                                        <tooltip title="Background color" />
				                    </button>

                                    <sketch-picker v-model="iconBackgroundColor" />
                                </dropdown>

                                <input
                                    v-control.block
                                    :value="iconBackgroundColorCssValue || null"
                                />
                            </div>
                        </control-group>
                    </div>

                    <div v-show="avatarType == 2">
                        <control-group>
                            <image-upload
                                :width="280"
                                :cropped-width="80"
                                :cropped-height="80"
                                :border-radius="0"
                                :realtime="true"
                                @change="onImageUploadChange"
                            />
                        </control-group>
                    </div>

                </div>

                <div class="col col-right">                    
                    <control-group title="Preview" orientation="vertical">
                        <div class="preview">
                            <group-avatar
                                :name="group.reportName"
                                :value="iconText"
                                :src="iconImage"
                                :size="40"
                                :styles="letterAvatarStyles"
                                :title="group.reportName"
                                @change="onLetterAvatarChange"
                            />
                        </div>
                    </control-group>

                </div>
            </div>

		</modal-body>

		<modal-actions>
			<btn v-if="ready" primary @click="save">Save</btn>
			<btn link @click="cancel">Cancel</btn>
		</modal-actions>
		
	</modal>
</template>


<script>
    import _ from 'lodash';
    import { Sketch } from 'vue-color';
    import { maxLength } from 'validators';
    import { AsyncMixin, FormValidationMixin, ModalMixin } from 'pdk/mixins';
    import { notifications } from 'pdk/services';
    import { groups } from '@/services';
    import { actions as dashboardActions } from 'dashboard-pdk/store';
    import { ImageUpload } from '@/components/images';
    import { GroupAvatar } from '@/components/groups';


	export default {
        name: 'edit-group-icon-modal',
        components: {
            'sketch-picker': Sketch,
            ImageUpload,
            GroupAvatar
        },
		mixins: [AsyncMixin, FormValidationMixin, ModalMixin],
		props: {
            locationGroupId: {
                type: String,
                required: true
            }
        },
        data() {
            return {
                group: undefined,
                ready: false,
                avatarType: 1,
                originalIconImage: undefined,
                iconImage: undefined,
                iconText: undefined,
                iconTextColor: '#ffffff',
                iconBackgroundColor: undefined
            };
        },
        validations: {
            iconText: {
                maxLength: maxLength(3)
            }
        },
        computed: {
            iconTextColorCssValue() {
                const color = this.iconTextColor;
                if (!color) return null;

                if (typeof color === 'string') return color;
                if (!color.rgba || color.rgba.a == 1) return color.hex;
                
                return `rgba(${color.rgba.r}, ${color.rgba.g}, ${color.rgba.b}, ${color.rgba.a})`;
            },
            iconBackgroundColorCssValue() {
                const color = this.iconBackgroundColor;
                if (!color) return null;

                if (typeof color === 'string') return color;
                if (!color.rgba || color.rgba.a == 1) return color.hex;
                
                return `rgba(${color.rgba.r}, ${color.rgba.g}, ${color.rgba.b}, ${color.rgba.a})`;
            },
            letterAvatarStyles() {
                const styles = {};

                if (this.iconTextColorCssValue) styles['color'] = this.iconTextColorCssValue;
                if (this.iconBackgroundColorCssValue) styles['background-color'] = this.iconBackgroundColorCssValue;

                return styles;
            }
        },
        watch: {
            avatarType: {
                immediate: true,
                handler(value) {
                    if (value == 1) {
                        this.iconImage = null;
                    }
                    else if (value == 2) {
                        this.iconImage = this.originalIconImage;
                    }
                }
            }
        },
        methods: {
            onOpen() {
                this.async(groups.getGroup({
                    locationGroupID: this.locationGroupId
                })).then((group) => {
                    this.group = group;

                    this.iconImage = this.originalIconImage = group.iconImage;
                    if(group.iconText) this.iconText = group.iconText;
                    this.iconTextColor = group.iconTextColor || '#FFFFFF';
                    if(group.iconBackgroundColor) this.iconBackgroundColor = group.iconBackgroundColor;
                    this.avatarType = this.iconImage ? 2 : 1;

                    this.ready = true;
                });
            },
            browseFiles() {
                this.$refs.uploader.triggerBrowseFiles();
            },
            save() {
                if (!this.validate()) return;

                return this.async(groups.updateGroupIcon({
                    locationGroupID: this.locationGroupId,
                    iconImage: this.iconImage,
                    iconText: this.iconText,
                    iconBackgroundColor: this.iconBackgroundColorCssValue,
                    iconTextColor: this.iconTextColorCssValue
                })).then(() => {
                    notifications.open({
                        content: 'Icon updated'
                    });

                    this.$store.dispatch(dashboardActions.LOCATION_GROUPS_LOAD);

                    this.$emit('save');

                    this.close();
                });
            },
            onLetterAvatarChange(e) {
                if (typeof this.iconText === 'undefined') this.iconText = e.text;
                if (typeof this.iconBackgroundColor === 'undefined') this.iconBackgroundColor = e.backgroundColor;
            },
            onImageUploadChange(dataUrl) {
                this.iconImage = dataUrl;
            }
		}
	};
</script>

<style lang="scss">
@import '~@/assets/styles/references';


.edit-group-icon-modal {
    .col-left { padding-right: gx(1); min-width: 310px; }
    .col-right { padding-left: gx(1); min-width: 140px; border-left: 1px solid color(black, divider); }
    .preview { width: 120px; height: 80px; background-color: color(primary, 900); display: flex; align-items: center; justify-content: center; border-radius: $border-radius; }

    @include media-breakpoint-down(xs) {
        .col-left { padding-right: 0; }
        .col-right { padding-left: 0; border-left: 0; }
    }
}

.color-picker-editor { position: relative;
    > .dropdown { position: absolute; right: 16px; margin-top: 8px; }
    .control {  }
}

.btn-color-picker-toggle { position: relative; background: none; border: 0; width: 18px; height: 18px; cursor: pointer;
    &:focus,
    &.focus { outline: 0; }

    .color-picker-preview,
    .color-picker-preview-transparency { width: 18px; height: 18px; border-radius: 9px; border: 1px solid color(black, divider); }

    .color-picker-preview { position: absolute; z-index: 1; }
    .color-picker-preview-transparency { background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMElEQVQ4T2N89uzZfwY8QFJSEp80A+OoAcMiDP7//483HTx//hx/Ohg1gIFx6IcBALl+VXknOCvFAAAAAElFTkSuQmCC"); }
}
</style>
